/**
 * Generic Bulk Upload helper.
 *
 * Every retrofitted upload endpoint returns:
 *   { summary, errors_preview, report_xlsx_b64, ... }
 *
 * Use `uploadWithReceipt(url, formData)` to POST the file, auto-download the
 * receipt workbook, and surface a one-line toast summary. The original
 * response object is returned so existing logic can keep using its bespoke
 * fields (created/updated/skipped/etc.).
 */
import axios from "axios";
import { toast } from "sonner";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Convert a base64 string to a Blob and trigger a browser download.
 */
export function downloadBase64Xlsx(b64, filename) {
  if (!b64) return false;
  try {
    const byteChars = atob(b64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i += 1) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: XLSX_MIME });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Build a sensible filename from the endpoint URL.
 *   /api/vendors/bulk-upload  ->  vendors-upload-receipt-20260508-143012.xlsx
 */
export function suggestReceiptFilename(url) {
  let slug = "bulk";
  try {
    const u = new URL(url, window.location.origin);
    const parts = u.pathname
      .split("/")
      .filter((p) => p && p !== "api" && p !== "bulk-upload" && p !== "upload");
    if (parts.length) slug = parts.join("-");
  } catch (e) {
    /* fall through */
  }
  const ts = new Date()
    .toISOString()
    .replace(/[:T]/g, "-")
    .split(".")[0];
  return `${slug}-upload-receipt-${ts}.xlsx`;
}

/**
 * Format a one-line toast message from the response summary.
 */
export function summaryToastText(summary, errorsPreview = []) {
  if (!summary) return "Upload complete.";
  const parts = [];
  if (summary.success) parts.push(`${summary.success} succeeded`);
  if (summary.skipped) parts.push(`${summary.skipped} skipped`);
  if (summary.errors) parts.push(`${summary.errors} errored`);
  const head = parts.length
    ? parts.join(" · ")
    : `${summary.total ?? 0} processed`;
  if (summary.errors && errorsPreview.length) {
    return `${head}. e.g. ${errorsPreview[0]}`;
  }
  return head;
}

/**
 * High-level helper.
 *   const data = await uploadWithReceipt(url, formData);
 *
 * Options:
 *   - autoDownload (default true)        — toggle the auto-download
 *   - filename     (default suggested)   — override receipt filename
 *   - axiosConfig  ({})                  — passed straight to axios.post
 *   - showToast    (default true)        — toggle the summary toast
 */
export async function uploadWithReceipt(url, formData, opts = {}) {
  const {
    autoDownload = true,
    filename,
    axiosConfig = {},
    showToast = true,
  } = opts;

  const res = await axios.post(url, formData, axiosConfig);
  const data = res.data || {};
  const summary = data.summary;
  const preview = data.errors_preview || [];
  const b64 = data.report_xlsx_b64;

  if (autoDownload && b64) {
    downloadBase64Xlsx(b64, filename || suggestReceiptFilename(url));
  }

  if (showToast && summary) {
    const text = summaryToastText(summary, preview);
    if (summary.errors > 0) {
      toast.warning(text + (b64 ? " — Receipt downloaded." : ""), {
        duration: 8000,
      });
    } else if (summary.skipped > 0) {
      toast.info(text + (b64 ? " — Receipt downloaded." : ""), {
        duration: 6000,
      });
    } else {
      toast.success(text + (b64 ? " — Receipt downloaded." : ""), {
        duration: 5000,
      });
    }
  }

  return data;
}
